import {
  Award01Icon,
  GiftIcon,
  LockIcon,
  Ticket01Icon,
  UserGroupIcon
} from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'

import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { CardHint } from '@/shared/ui/CardHint'
import {
  FeaturedListCardStrip,
  featuredListCardClassName
} from '@/shared/ui/FeaturedListCardStrip'

import { PRIZE_STATUS_LABELS } from '../constants/status'
import { prizeBadgeVariant } from '../lib/prizeBadgeVariant'
import { getPrizeParticipationHint } from '../lib/prizeParticipationHint'
import { getPrizeEligibleParticipantsLabel } from '../lib/prizeRuleMessage'

import type { PrizeListItem } from '../hooks/usePrizesListView'

type PrizeListCardProps = {
  prize: PrizeListItem
  featured?: boolean
  onShow: () => void
}

const buttonVariantForStatus = (
  uiStatus: PrizeListItem['uiStatus']
):
  | 'primary'
  | 'primary-ghost'
  | 'success'
  | 'success-ghost'
  | 'secondary'
  | undefined => {
  if (uiStatus === 'available') return 'primary'
  if (uiStatus === 'progress') return 'primary-ghost'
  if (uiStatus === 'success') return 'success'
  if (uiStatus === 'expired') return 'secondary'
  if (uiStatus === 'locked') return 'secondary'
  return undefined
}

const ctaLabelForPrize = (
  prize: PrizeListItem,
  featured?: boolean
): string | null => {
  const { uiStatus } = prize

  if (uiStatus === 'locked') return 'Detalhes'
  if (uiStatus === 'success') return featured ? 'Reservar prêmio' : 'Reservar'
  if (uiStatus === 'expired') return 'Ver resultado'
  if (uiStatus === 'progress') return 'Detalhes'
  if (uiStatus === 'available') return featured ? 'Detalhes' : 'Participar'

  return null
}

const ctaVariantForPrize = (
  prize: PrizeListItem,
  featured?: boolean
): ReturnType<typeof buttonVariantForStatus> => {
  const label = ctaLabelForPrize(prize, featured)
  if (featured && label === 'Detalhes') return 'primary'
  return buttonVariantForStatus(prize.uiStatus)
}

const participationHintIcon = (uiStatus: PrizeListItem['uiStatus']) => {
  if (uiStatus === 'progress') {
    return <Ticket01Icon size={14} variant="duotone" aria-hidden />
  }
  if (uiStatus === 'locked') {
    return <LockIcon size={14} variant="duotone" aria-hidden />
  }
  if (uiStatus === 'success') {
    return <Award01Icon size={14} variant="duotone" aria-hidden />
  }
  if (uiStatus === 'available') {
    return <GiftIcon size={14} variant="duotone" aria-hidden />
  }

  return undefined
}

const participationHintClassName = (uiStatus: PrizeListItem['uiStatus']) =>
  clsx(
    'font-semibold',
    uiStatus === 'progress' && 'text-primary',
    uiStatus === 'available' && 'text-gray-700',
    uiStatus === 'locked' && 'text-gray-700',
    uiStatus === 'success' && 'text-status-success',
    uiStatus === 'expired' && 'text-gray-700'
  )

export const PrizeListCard = ({
  prize,
  featured = false,
  onShow
}: PrizeListCardProps) => {
  const { name, media, uiStatus } = prize

  const imageUrl = _.get(media, 'file.url', '/placeholders/reward.jpg')
  const isLocked = uiStatus === 'locked'
  const isClosed = uiStatus === 'expired'
  const isWon = uiStatus === 'success'
  const eligibleParticipantsLabel = getPrizeEligibleParticipantsLabel(
    prize.prize_rules
  )
  const participationHint = getPrizeParticipationHint(
    uiStatus,
    prize.ineligibilityHint
  )
  const railParticipationHint =
    uiStatus === 'locked' ? null : participationHint
  const ineligibilityMessage =
    uiStatus === 'locked' ? prize.ineligibilityHint : null

  const ctaLabel = ctaLabelForPrize(prize, featured)
  const ctaVariant = ctaVariantForPrize(prize, featured)
  const showCta = ctaLabel && ctaVariant

  return (
    <Card
      flush={featured}
      onClick={onShow}
      className={clsx(
        'cursor-pointer',
        featured && !isWon && featuredListCardClassName('prize'),
        !featured && 'shadow-sm',
        isWon &&
          'border-2 border-green-200 bg-gradient-to-br from-green-50/80 to-white',
        isClosed && 'opacity-90',
        isLocked && 'opacity-95'
      )}
    >
      {featured && (
        <FeaturedListCardStrip label="Prêmio em destaque" tone="prize" />
      )}

      <Card.Body>
        <div className="flex items-stretch gap-2 sm:gap-3">
          <div className="flex aspect-square w-20 shrink-0 self-start overflow-hidden rounded-lg">
            <img
              src={imageUrl}
              alt=""
              className={clsx(
                'h-full w-full object-cover',
                (isClosed || isLocked) && 'opacity-65 grayscale'
              )}
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              {uiStatus !== 'available' && (
                <Badge
                  variant={prizeBadgeVariant(uiStatus)}
                  size="sm"
                  showIcon={false}
                >
                  {PRIZE_STATUS_LABELS[uiStatus]}
                </Badge>
              )}
            </div>

            <h3
              className={clsx(
                'm-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900',
                (isClosed || isLocked) && 'text-gray-700'
              )}
            >
              {name}
            </h3>

            {eligibleParticipantsLabel && (
              <span className="inline-flex items-center gap-1 text-2xs text-gray-700">
                <UserGroupIcon size={11} variant="stroke" aria-hidden />
                {eligibleParticipantsLabel}
              </span>
            )}

            {ineligibilityMessage && (
              <p className="m-0 flex items-start gap-1.5 text-2xs leading-snug text-gray-700">
                <LockIcon size={12} variant="duotone" aria-hidden />
                <span>{ineligibilityMessage}</span>
              </p>
            )}
          </div>

          <div className="flex min-h-20 w-[var(--rail-w-sm)] shrink-0 flex-col">
            <div className="flex flex-1 flex-col items-end justify-center gap-0.5 text-right">
              {railParticipationHint && (
                <CardHint
                  icon={participationHintIcon(uiStatus)}
                  className={participationHintClassName(uiStatus)}
                >
                  {railParticipationHint}
                </CardHint>
              )}
            </div>

            <div className="flex flex-col items-stretch">
              {showCta && (
                <Button
                  variant={ctaVariant}
                  size="sm"
                  className="w-full justify-center font-bold!"
                  onClick={e => {
                    e.stopPropagation()
                    onShow()
                  }}
                >
                  {ctaLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
